/**
 * `GameState`: the single state shape shared by every tier (docs/design/03 §3, 02 §3).
 *
 * Plain JSON only: serialisable with `JSON.stringify`, hashable with `hashState`, restorable after a
 * Durable Object hibernates. All in-flight work (setup decisions, the chain, interpreter frames, undo
 * requests) lives here, never in closures (ADR-0001). Treat a `GameState` as immutable: `apply`
 * returns a new state and never mutates its input.
 */
import type { ChainItem } from './chain';
import type { Decision } from './decisions';
import type { BattlefieldId, CardId, ObjectId, PlayerId } from './ids';
import type { GameObject } from './objects';
import type { ModeConfig } from './mode';
import type { PoolUnit } from './resources';
import type {
  ContinuousEffect,
  DelayedAbility,
  Frame,
  PendingTrigger,
  ShowdownState,
  Task,
  TurnHistory,
} from './rules-slots';
import type { DeckCard, RulesFlags, SeatSettings, Tier } from './setup';

/** Phases of a turn (CR 314-317; research 01 §7.3). */
export const PHASES = ['awaken', 'beginning', 'channel', 'draw', 'main', 'ending'] as const;
export type Phase = (typeof PHASES)[number];

/**
 * Domain-separated PRNG streams derived from the match seed (03 §12). `ids` is not advanced: its words
 * key the object-id permutation.
 */
export const RNG_STREAMS = ['firstPlayer', 'battlefields', 'shuffle', 'randomOrder', 'ids', 'cardRandom'] as const;
export type RngStreamName = (typeof RNG_STREAMS)[number];
/** xoshiro128** state (four unsigned 32-bit words) per stream. Never leaves the server. */
export type RngState = Record<RngStreamName, [number, number, number, number]>;

/** Facts fixed at match creation (the tier can only be lowered). */
export interface MatchMeta {
  matchId: string;
  mode: ModeConfig;
  /** Rules tier (02 §1). Fixed at creation; only lowered by mutual consent (02 §2.2). */
  tier: Tier;
  flags: RulesFlags;
  versions: { engine: string; rules: string; catalog: string };
  /** H(seed ‖ matchId), published before the first shuffle (03 §12). */
  commitment: string;
}

/** Turn markers (CR 302-317). */
export interface TurnState {
  /** 0 during setup, 1 for the first player's first turn, and so on (turn number = turns started). */
  number: number;
  /** The turn player (CR 304). During setup: the first player once turn order is known. */
  activePlayer: PlayerId;
  phase: Phase;
  /** Sub-step within the phase (tier 3, e.g. `scoring`), or `null`. */
  step: string | null;
  /** Turn order, starting with the first player (CR 115.1). */
  order: PlayerId[];
  /** Extra turns queued after the current one, in the order generated (CR 735-738). */
  queue: PlayerId[];
  /** `true` until that player's first turn has ended: first-turn adjustments apply (CR 487.7). */
  firstTurn: Record<PlayerId, boolean>;
}

/** Per-seat state that is not a zone (research 01 §5.4). */
export interface PlayerState {
  id: PlayerId;
  /** 0-based seating position. */
  seat: number;
  team: number | null;
  /** `conceded` and `eliminated` players are removed from the game (CR 650-652) and can't act. */
  status: 'playing' | 'conceded' | 'eliminated';
  /** Score (CR 194). Never negative (CR 194.4). */
  points: number;
  /** Experience, a public player resource (CR 728-733). Never negative. */
  xp: number;
  /** Unspent Energy and Power (CR 166). Tier 1 never fills it. */
  pool: PoolUnit[];
  /** First-turn adjustments from the mode and turn order (CR 487.7, 489.7), applied by hand in tier 1. */
  firstTurnAdjustment: { extraChannel: number; skipDraw: boolean };
  settings: SeatSettings;
  /**
   * The card id of the player's Chosen Champion (CR 103.3, 111), or `null`: "your Chosen Champion" stays
   * that card wherever it goes (Hallowed Tomb). Schema 3.
   */
  chosenChampion: CardId | null;
}

/** A player's own zones (CR 107-108). Lists hold object ids; decks have their top at index 0. */
export interface PlayerZones {
  mainDeck: ObjectId[];
  runeDeck: ObjectId[];
  hand: ObjectId[];
  /** Newest last. */
  trash: ObjectId[];
  banishment: ObjectId[];
  /** The Champion Zone: the Chosen Champion until it is played (CR 108.3). */
  champion: ObjectId[];
  /** The Champion Legend first, then legends added by effects (CR 107.4.d). */
  legend: ObjectId[];
  /** Permanents in this player's base, in arrival order (CR 107.1). */
  base: ObjectId[];
  /** Channeled runes, part of the base (CR 107.1.c). */
  runes: ObjectId[];
}

/** A battlefield location and its card (CR 169-172, 190, 199; research 01 §10.9). */
export interface BattlefieldState {
  id: BattlefieldId;
  /** The battlefield card (a game object at this location, CR 199.1). Replaced by Brush-style effects (CR 439). */
  card: ObjectId;
  /** `null` while uncontrolled (CR 190.2). */
  controller: PlayerId | null;
  /** The player who applied Contested, or `null` (CR 190.3; tier 3). */
  contestedBy: PlayerId | null;
  /** Permanents at this battlefield (units, attached gear…) in arrival order; group by controller to draw sides. */
  permanents: ObjectId[];
  /** The Facedown Zone (CR 107.3). */
  facedown: ObjectId[];
  /** Facedown Zone capacity, 1 by default (CR 107.3.b). Not enforced in tier 1. */
  facedownCapacity: number;
  /** Players who scored this battlefield this turn (CR 472). Cleared when a turn ends. */
  scoredThisTurn: PlayerId[];
  /** A showdown is staged here (CR 323.7; tier 3). */
  showdownStaged: boolean;
  /** A combat is staged here (CR 323.8; tier 3). */
  combatStaged: boolean;
}

/** Setup progress (CR 110-118; research 01 §6). `null` once the first turn has started. */
export interface SetupState {
  step: 'battlefields' | 'mulligan';
  /** Players still to act in this step, in the order they will be asked. */
  pending: PlayerId[];
  /** Battlefields each player brought, offered under the `choose` policy (CR 488.5). Private to each player. */
  battlefieldOptions: Record<PlayerId, DeckCard[]>;
  /** Each player's pick (index into their options). Private until every player has picked. */
  battlefieldPicks: Record<PlayerId, number | null>;
  /** Cards each player replaced in their mulligan (`null`: not yet decided). Public (CR 108.7.e). */
  mulligans: Record<PlayerId, number | null>;
}

/** Undo by opponent consent (owner decision Q47; TR 509). See `table/commands/undo.ts`. */
export interface UndoState {
  /** Undoable steps since the last undo barrier, oldest first (bounded). */
  history: UndoEntry[];
  /** An open request waiting for the other players' consent. */
  request: UndoRequest | null;
}

/** The state before one undoable step, without its own undo history. */
export type UndoSnapshot = Omit<GameState, 'undo'>;

/** One undoable step: who did it and the state before it. */
export interface UndoEntry {
  /** `seq` of the state the step produced. */
  seq: number;
  /** Who performed the step. Only they may ask to undo it. */
  by: PlayerId;
  /** The command type (`move`, `exhaust`, …), for the log and the UI. */
  command: string;
  /** Undoable without consent (tier-2 automatic bookkeeping, Q47). Tier 1 never sets it. */
  free: boolean;
  before: UndoSnapshot;
}

/** An undo request waiting for consent. */
export interface UndoRequest {
  by: PlayerId;
  /** How many of `by`'s most recent steps to undo. */
  count: number;
  /** `seq` when the request was made. */
  seq: number;
  /** Players who still have to answer. */
  awaiting: PlayerId[];
}

/** Why a game ended. `noResult` and `aborted` record no winner (Q26: abandoned before turn 3). */
export type GameEndReason = 'points' | 'concede' | 'timeout' | 'abandon' | 'draw' | 'noResult' | 'aborted';

/** How the game ended. */
export interface GameResult {
  /** Winning players (one in 1v1; a team in 2v2; empty for a draw or no result). */
  winners: PlayerId[];
  reason: GameEndReason;
  /** `seq` of the state that ended the game. */
  seq: number;
  /** Turn number when it ended. */
  turn: number;
  points: Record<PlayerId, number>;
}

/** The whole game (see the module header). */
export interface GameState {
  /** State schema version (03 §15). */
  schema: number;
  meta: MatchMeta;
  /** The only randomness source (03 §12). */
  rng: RngState;
  /** Increments on every applied command. */
  seq: number;
  /**
   * Logical counters. Object ids are a keyed permutation of `nextObjectId` (see `core/alloc.ts`); the
   * other ids are sequential. Never sent to clients.
   */
  clock: {
    nextObjectId: number;
    nextTimestamp: number;
    nextChainItem: number;
    nextDecision: number;
    nextBattlefield: number;
  };
  setup: SetupState | null;
  turn: TurnState;
  players: Record<PlayerId, PlayerState>;
  objects: Record<ObjectId, GameObject>;
  zones: Record<PlayerId, PlayerZones>;
  battlefields: BattlefieldState[];
  chain: ChainItem[];
  /** Tier 3: the showdown or combat in progress. */
  showdown: ShowdownState | null;
  /**
   * Who holds priority (CR 312). Tier 3 enforces it. In tiers 1-2 it is a marker: `null` means the turn
   * player acts; a seat means that player was passed priority (e.g. to respond to a stacked card).
   */
  priority: PlayerId | null;
  continuous: ContinuousEffect[];
  delayed: DelayedAbility[];
  tasks: Task[];
  pendingTriggers: PendingTrigger[];
  frames: Frame[];
  /** What the engine is waiting for (setup decisions in every tier; everything in tier 3). */
  decision: Decision | null;
  history: TurnHistory;
  undo: UndoState;
  result: GameResult | null;
}
